import { useEffect, useMemo, useState } from 'react'
import { useAuth } from '../../../auth/AuthContext'
import type { CalendarioDTO, CalendarioEventoDTO, ResumenCarteraResponse, WrapperResponse } from '../../../model/kartera-api-model'
import { useHistory } from '../../../router/history'
import APIInvoke from '../../../utils/APIInvoke'
import * as Permisos from '../../../utils/Permisos'
import * as Roles from '../../../utils/Roles'
import {
    AVATAR_TONES,
    compactAmount,
    currentMonthName,
    currentYear,
    daysFromToday,
    errorMessage,
    formatDayMonth,
    formatWeekdayName,
    initials,
    monthSegmentsIso,
    shiftIso,
    SUMMARY_PATH,
    todayIso,
} from '../common/summary-common'

const LOAD_ERROR = 'No se pudo cargar el resumen de la cartera.'
const BIRTHDAYS_ERROR = 'No se pudieron cargar los cumpleaños de la semana.'
const BIRTHDAY_DAYS = 7

export interface BirthdayView {
    clienteId: number
    name: string
    initials: string
    tone: string
}

export interface BirthdayDayView {
    date: string
    label: string
    dateLabel: string
    today: boolean
    people: BirthdayView[]
}

/** El backend arma el nombre con `String.format`, así que los apellidos vacíos llegan como `null`. */
const birthdayName = (message: string | null) => String(message || '').replace(/\bnull\b/g, ' ').replace(/\s+/g, ' ').trim()

const RELATIVE_DAYS: Record<number, string> = { 0: 'Hoy', 1: 'Mañana' }

/** El filtro de cumpleaños del backend compara día y mes por separado, así que cada petición no debe cruzar de mes. */
const fetchBirthdays = async (from: string, to: string): Promise<CalendarioEventoDTO[]> => {
    const pages = await Promise.all(monthSegmentsIso(from, to).map(async (segment) => {
        const payload: WrapperResponse<CalendarioDTO> | null = await (await APIInvoke.invokeGET(`/calendario?inicio=${segment.from}&fin=${segment.to}&type=BIRTHDAY`)).json()
        if (!payload?.ok || !payload.body) throw new Error(payload?.message || BIRTHDAYS_ERROR)
        return payload.body.eventos || []
    }))
    return pages.flat()
}

const toBirthdayDays = (events: CalendarioEventoDTO[]): BirthdayDayView[] => {
    const people = events
        .flatMap((event) => (event.type === 'birthday' && event.clienteId != null && event.date
            ? [{ clienteId: event.clienteId, date: event.date, name: birthdayName(event.message) || 'Cliente' }]
            : []))
        .sort((left, right) => left.date.localeCompare(right.date) || left.name.localeCompare(right.name, 'es'))

    const days = new Map<string, BirthdayDayView>()
    people.forEach((person, index) => {
        let day = days.get(person.date)
        if (!day) {
            const offset = daysFromToday(person.date) ?? 0
            day = {
                date: person.date,
                label: RELATIVE_DAYS[offset] || formatWeekdayName(person.date) || '',
                dateLabel: formatDayMonth(person.date) || '',
                today: offset === 0,
                people: [],
            }
            days.set(person.date, day)
        }
        day.people.push({
            clienteId: person.clienteId,
            name: person.name,
            initials: initials(person.name),
            tone: AVATAR_TONES[index % AVATAR_TONES.length],
        })
    })
    return Array.from(days.values())
}

export type SummaryRamo = 'vida' | 'auto' | 'salud' | 'diversos'

export const SUMMARY_RAMOS: Array<{ id: SummaryRamo; label: string; ramo: string }> = [
    { id: 'vida', label: 'Vida', ramo: 'vida' },
    { id: 'auto', label: 'Autos', ramo: 'autos' },
    { id: 'salud', label: 'Salud', ramo: 'salud' },
    { id: 'diversos', label: 'Daños', ramo: 'daños' },
]

const ALL_RAMOS = SUMMARY_RAMOS.map((item) => item.id)

/** Cubetas del backend, de la más urgente a la más lejana. */
export const AGING_BUCKETS: Array<{ id: '0' | '5' | '15' | '30' | '45'; label: string; tone: string }> = [
    { id: '0', label: 'Vencidos', tone: 'bg-k-danger-soft text-k-danger-ink' },
    { id: '5', label: '0–14 días', tone: 'bg-tone-orange text-tone-orange-ink' },
    { id: '15', label: '15–29 días', tone: 'bg-tone-pink text-tone-pink-ink' },
    { id: '30', label: '30–44 días', tone: 'bg-tone-blue text-tone-blue-ink' },
    { id: '45', label: '45+ días', tone: 'bg-tone-teal text-tone-teal-ink' },
]

export interface AgingView {
    id: string
    label: string
    tone: string
    count: number
}

export interface GoalView {
    id: string
    title: string
    amount: string
    goal: string | null
    progress: number
    percent: string
}

const emptySummary: ResumenCarteraResponse = {
    clientes: 0, polizas: 0,
    cxc45: 0, cxc30: 0, cxc15: 0, cxc5: 0, cxc0: 0,
    ren45: 0, ren30: 0, ren15: 0, ren5: 0, ren0: 0,
    ppm: 0, paa: 0, em: 0, eaa: 0, metaAnual: 0, metaMensual: 0,
}

const aging = (summary: ResumenCarteraResponse, prefix: 'cxc' | 'ren'): AgingView[] =>
    AGING_BUCKETS.map((bucket) => ({
        ...bucket,
        count: Number(summary[`${prefix}${bucket.id}` as keyof ResumenCarteraResponse]) || 0,
    }))

const goal = (id: string, title: string, amount: number | null, target: number | null): GoalView => {
    const value = Number(amount) || 0
    const meta = Number(target) || 0
    const progress = meta > 0 ? value / meta : 0
    return {
        id,
        title,
        amount: compactAmount(value),
        goal: meta > 0 ? compactAmount(meta) : null,
        progress,
        percent: meta > 0 ? `${Math.round(progress * 100)}%` : 'Sin meta',
    }
}

export const useSummaryDashboardModel = () => {
    const history = useHistory()
    const { user, hasRole, canAccess } = useAuth()
    const unsubscribed = hasRole(Roles.ROLE_UNSUBSCRIBE_USER) && !hasRole(Roles.ROLE_USER)
    const canBirthdays = canAccess([Permisos.CALENDARIO_R])
    const [birthdays, setBirthdays] = useState<BirthdayDayView[]>([])
    const [birthdaysLoading, setBirthdaysLoading] = useState(false)
    const [birthdaysError, setBirthdaysError] = useState<string | null>(null)
    const [clienteId, setClienteId] = useState<number | null>(null)

    const [ramos, setRamos] = useState<SummaryRamo[]>(ALL_RAMOS)
    const [reloadToken, setReloadToken] = useState(0)
    const [loading, setLoading] = useState(!unsubscribed)
    const [error, setError] = useState<string | null>(null)
    const [summary, setSummary] = useState<ResumenCarteraResponse>(emptySummary)

    useEffect(() => {
        if (unsubscribed) return undefined
        let active = true
        const load = async () => {
            try {
                setLoading(true)
                setError(null)
                const payload: WrapperResponse<ResumenCarteraResponse> | null = await (await APIInvoke.invokeGET(`/reportes/resumen-cartera?tipo=${ramos.join(',')}`)).json()
                if (!active) return
                if (!payload?.ok || !payload.body) throw new Error(payload?.message || LOAD_ERROR)
                setSummary(payload.body)
            } catch (loadError) {
                if (active) setError(errorMessage(loadError, LOAD_ERROR))
            } finally {
                if (active) setLoading(false)
            }
        }
        load()
        return () => { active = false }
    }, [ramos, reloadToken, unsubscribed])

    useEffect(() => {
        if (unsubscribed || !canBirthdays) return undefined
        let active = true
        const load = async () => {
            try {
                setBirthdaysLoading(true)
                setBirthdaysError(null)
                const events = await fetchBirthdays(todayIso(), shiftIso(BIRTHDAY_DAYS - 1))
                if (!active) return
                setBirthdays(toBirthdayDays(events))
            } catch (loadError) {
                if (active) setBirthdaysError(errorMessage(loadError, BIRTHDAYS_ERROR))
            } finally {
                if (active) setBirthdaysLoading(false)
            }
        }
        load()
        return () => { active = false }
    }, [reloadToken, unsubscribed, canBirthdays])

    /** Con todos los ramos activos, el primer clic deja solo ese ramo; después alterna sin quedar en cero. */
    const toggleRamo = (id: SummaryRamo) => setRamos((current) => {
        if (current.length === ALL_RAMOS.length) return [id]
        if (current.includes(id)) return current.length === 1 ? current : current.filter((item) => item !== id)
        return ALL_RAMOS.filter((item) => item === id || current.includes(item))
    })

    const month = currentMonthName()
    const year = currentYear()
    const goals = useMemo<GoalView[]>(() => [
        goal('ppm', `Primas pagadas · ${month}`, summary.ppm, summary.metaMensual),
        goal('paa', `Pagos acumulados · ${year}`, summary.paa, summary.metaAnual),
        goal('em', `Emisión · ${month}`, summary.em, summary.metaMensual),
        goal('eaa', `Emisión acumulada · ${year}`, summary.eaa, summary.metaAnual),
    ], [summary, month, year])

    return {
        name: user?.nombres || 'agente',
        unsubscribed,
        loading,
        error,
        ramos,
        allRamos: ramos.length === ALL_RAMOS.length,
        clientes: summary.clientes || 0,
        polizas: summary.polizas || 0,
        cobranza: aging(summary, 'cxc'),
        renovaciones: aging(summary, 'ren'),
        goals,
        birthdayDays: birthdays,
        birthdayCount: birthdays.reduce((total, day) => total + day.people.length, 0),
        birthdaysLoading,
        birthdaysError,
        weekLabel: `${formatDayMonth(todayIso())} – ${formatDayMonth(shiftIso(BIRTHDAY_DAYS - 1))}`,
        clienteId,
        openCliente: (id: number) => setClienteId(id),
        closeCliente: () => setClienteId(null),
        editCliente: (id: number) => history.push({ pathname: `/clientes/${id}/edit`, state: { backUrl: SUMMARY_PATH } }),
        can: {
            birthdays: canBirthdays,
            clientes: canAccess([Permisos.CLIENTES_R]),
            cobranza: canAccess([Permisos.POLIZAS_COBRANZA]),
            renovaciones: canAccess([Permisos.POLIZAS_RENOVACIONES]),
            metas: canAccess([Permisos.METAS]),
        },
        linkState: { backUrl: SUMMARY_PATH },
        toggleRamo,
        selectAllRamos: () => setRamos(ALL_RAMOS),
        reload: () => setReloadToken((token) => token + 1),
    }
}
